import React from "react";
import { dragZoneStyleSets, dropZoneStyleSets } from "../../../../config/controlStyles";
import { DragAndDropHelper } from "../../../../../../utilities/DragAndDropHelper";
import { useDispatch, useSelector } from "react-redux";
import { addUploadedFiles } from "../../../../redux/modules/file";
import { logger } from "../../../../../../utilities/logger";
import { IPageSettings } from "../../../../interfaces/IPageSettings";
import { IBreadcrumbItem } from "@fluentui/react";
import { ComponentTypes } from "../../../../config/config";
import { RootState } from "../../redux/storeMain";
import authModule from "../../../../../../utilities/AuthModule";
import { tryDecodeUriComponent } from "../../../../../../utilities/EncodeDecode";
import { getListItemWebUrl } from "../../../../services/GraphService";

interface IDroppableBreadcrumbItemProps {
    item: IBreadcrumbItem;
    directory: IPageSettings;
    onNavigate: (ev: any, item: IBreadcrumbItem) => void;
}

export const DroppableBreadcrumbItem: React.FC<IDroppableBreadcrumbItemProps> = ({ item, directory, children, onNavigate }) => {
    const dispatch = useDispatch();

    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const oneDriveWebUrl = useSelector((state: RootState) => state.user.oneDriveWebUrl);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const isValidDropZone = React.useMemo(() => {
        return directory.type === ComponentTypes.SharePointFolders
            || directory.type === ComponentTypes.TeamFolders
            || directory.type === ComponentTypes.OneDriveSubFolder
            || directory.type === ComponentTypes.OneDrive
    }, [directory]);

    const isOneDrive = React.useMemo(() => {
        return directory.type === ComponentTypes.OneDriveSubFolder
            || directory.type === ComponentTypes.OneDrive;
    }, [directory]);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event)) {
            event.preventDefault();
            event.stopPropagation();
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, []);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setIsDraggedOver(false);

        if (isValidDropZone) {
            try {
                if (DragAndDropHelper.validateDroppedFiles(event)) {
                    dispatch(addUploadedFiles(await DragAndDropHelper.handleDroppedFiles(event)));
                    onNavigate(null, item);
                }
                else if (DragAndDropHelper.validateDroppedGridItems(event)) {
                    let serverRelativePath: string;
                    if (directory.type === ComponentTypes.OneDrive) {
                        serverRelativePath = new URL(oneDriveWebUrl).pathname;
                    }
                    else if (!directory?.serverRelativePath && directory?.directory?.length > 0 && directory?.id) {
                        const driveId = isOneDrive && oneDriveRootDetails ? oneDriveRootDetails.driveId : (directory.parentId ? directory.parentId : directory.id);
                        const webUrl = await getListItemWebUrl(await authModule.getGraphAccessToken(), driveId, directory.parentId ? directory.id : null);
                        if (webUrl) {
                            serverRelativePath = tryDecodeUriComponent(new URL(webUrl).pathname);
                        }
                    }
                    else {
                        serverRelativePath = directory?.serverRelativePath;
                    }

                    if (serverRelativePath) {
                        DragAndDropHelper.destinationDirectory = directory;
                        const siteUrl = isOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : directory?.webUrl;
                        DragAndDropHelper.handleGridMove(dispatch, siteUrl, workingDirectories, isOneDrive, serverRelativePath);
                    }
                }
            } catch (error) {
                logger(error, "DroppableBreadcrumbItem (handleDrop)");
            }
        }
    }, [item, directory, oneDriveRootDetails, oneDriveWebUrl, isValidDropZone, isOneDrive, onNavigate]);

    const className = React.useMemo((): string => {
        let className: string = "dropzone";
        if (isDraggedOver) {
            className += isValidDropZone ? ` ${dropZoneStyleSets.dropzoneActive}` : ` ${dragZoneStyleSets.dragzoneActive}`;
        }
        return className;
    }, [isValidDropZone, isDraggedOver]);

    React.useEffect(() => {
        let debounceTimer;
        if (isDraggedOver) {
            debounceTimer = setTimeout(() => {
                onNavigate(null, item);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [item, isDraggedOver, onNavigate]);

    return (
        <div
            className={className}
            onDragEnter={dragEnter}
            onDragOver={dragOver}
            onDragLeave={dragLeave}
            onDrop={handleDrop}
        >
            {children}
        </div>
    );
};